import React, { useState, useEffect, useContext, useId } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Box,
  Button,
  Container,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMapLocationDot } from "@fortawesome/free-solid-svg-icons";

import { DataContext } from "../../../context/DataContext";
import { getRuta } from "../../../helpers/Helpers";
import { tienePermiso } from "../../../helpers/permisos";
import { getToken } from "../../../api/Account";
import { getDepartmentByIdAsync, updateMunicipalityAsync } from "../../../api/Locations";
import {
  CargandoTabla,
  EstadoError,
  EstadoVacio,
  PageHeader,
  useAviso,
} from "../../../components/base";
import {
  DialogoFormulario,
  encabezadoTabla,
  ocultoVisual,
  useCargaLocal,
  useEnvio,
  useEscritura,
} from "../comunes";

/** Diálogo para poner o cambiar la abreviatura de un municipio. */
const DialogoAbreviatura = ({ municipio, onCerrar, onGuardado }) => {
  const token = getToken();
  const escribir = useEscritura();
  const { guardando, enviar } = useEnvio();
  const id = useId();
  const original = municipio.abreviatura ?? "";
  const [valor, setValor] = useState(original);
  const [intentado, setIntentado] = useState(false);
  const cambio = valor.trim() !== original.trim();
  // El servidor la exige ([Required]) y el gráfico del inicio la usa como etiqueta.
  const errorCampo = intentado && valor.trim() === "" ? "Escriba la abreviatura" : "";

  const guardar = () =>
    enviar(async () => {
      setIntentado(true);
      if (!cambio || valor.trim() === "") return;
      const ok = await escribir(
        () => updateMunicipalityAsync(token, { id: municipio.id, Abreviatura: valor.trim() }),
        "No se pudo guardar la abreviatura"
      );
      if (ok) onGuardado(valor.trim());
    });

  return (
    <DialogoFormulario
      titulo={`Abreviatura de ${municipio.name}`}
      onCerrar={onCerrar}
      hayCambios={cambio}
      guardando={guardando}
      deshabilitado={!cambio}
      onEnviar={guardar}
      textoEnviar="Guardar abreviatura"
    >
      <TextField
        fullWidth
        autoFocus
        required
        id={`${id}-abreviatura`}
        label="Abreviatura"
        value={valor}
        onChange={(e) => setValor(e.target.value.toUpperCase())}
        error={Boolean(errorCampo)}
        helperText={errorCampo || "Es la etiqueta del municipio en el gráfico de clientes por ubicación del inicio"}
        disabled={guardando}
        inputProps={{ autoComplete: "off", maxLength: 10 }}
        sx={{ mt: 1 }}
      />
    </DialogoFormulario>
  );
};

const Municipalities = () => {
  let ruta = getRuta();
  const token = getToken();
  let navigate = useNavigate();
  const { id } = useParams();
  const { access } = useContext(DataContext);
  const aviso = useAviso();

  const [department, setDepartment] = useState(null);
  const [recargar, setRecargar] = useState(false);
  const [editando, setEditando] = useState(null);

  const puedeEditar = tienePermiso(access, "CONFIG UPDATE");

  const { cargando, error, cargar } = useCargaLocal(() => getDepartmentByIdAsync(token, id));

  useEffect(() => {
    let vigente = true;
    (async () => {
      const result = await cargar();
      if (vigente && result) setDepartment(result.data.result);
    })();
    return () => {
      vigente = false;
    };
  }, [recargar, id]);

  const municipios = department?.municipalities ?? [];
  const verComunidades = (mun) => navigate(`${ruta}/locations/municipalities/${mun.id}`);

  let contenido;
  if (cargando) {
    contenido = <CargandoTabla filas={8} columnas={4} etiqueta="Cargando municipios…" />;
  } else if (error) {
    contenido = (
      <EstadoError
        titulo={
          error === "conexion"
            ? "No se pudo conectar con el servidor"
            : "No se pudieron cargar los municipios"
        }
        onReintentar={() => setRecargar((actual) => !actual)}
      />
    );
  } else if (municipios.length === 0) {
    contenido = (
      <EstadoVacio
        titulo="Este departamento no tiene municipios"
        descripcion="Aquí aparecen sus municipios con su abreviatura y sus comunidades."
      />
    );
  } else {
    contenido = (
      <TableContainer>
        <Table
          aria-label={`Municipios de ${department.name}`}
          sx={{ "& .MuiTableCell-root": { px: { xs: 1, sm: 2 } } }}
        >
          <TableHead>
            <TableRow>
              <TableCell sx={encabezadoTabla}>Municipio</TableCell>
              <TableCell sx={encabezadoTabla}>Abreviatura</TableCell>
              <TableCell sx={{ ...encabezadoTabla, textAlign: "right", display: { xs: "none", sm: "table-cell" } }}>
                Comunidades
              </TableCell>
              <TableCell sx={{ ...encabezadoTabla, textAlign: "right" }}>
                <Box component="span" sx={ocultoVisual}>
                  Acciones
                </Box>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {municipios.map((row) => (
              <TableRow key={row.id} hover>
                <TableCell component="th" scope="row">
                  <Box sx={{ fontWeight: 700 }}>{row.name}</Box>
                  <Box
                    component="span"
                    sx={{ typography: "caption", color: "text.secondary", display: { xs: "block", sm: "none" } }}
                  >
                    {`${row.communities?.length ?? 0} comunidades`}
                  </Box>
                </TableCell>
                <TableCell sx={{ whiteSpace: { xs: "normal", sm: "nowrap" } }}>
                  {row.abreviatura ? row.abreviatura : <Box component="span" sx={{ color: "text.secondary" }}>
                      <Box component="span" sx={{ display: { xs: "none", sm: "inline" } }}>Sin abreviatura</Box>
                      <Box component="span" aria-hidden="true" sx={{ display: { xs: "inline", sm: "none" } }}>—</Box>
                      <Box component="span" sx={{ ...ocultoVisual, display: { xs: "inline", sm: "none" } }}>Sin abreviatura</Box>
                    </Box>}
                  {puedeEditar ? (
                    <Tooltip title={row.abreviatura ? "Cambiar abreviatura" : "Poner abreviatura"}>
                      <IconButton
                        aria-label={`${row.abreviatura ? "Cambiar" : "Poner"} la abreviatura de ${row.name}`}
                        onClick={() => setEditando(row)}
                        sx={{ ml: 0.5, color: "primary.main" }}
                      >
                        <EditOutlinedIcon />
                      </IconButton>
                    </Tooltip>
                  ) : null}
                </TableCell>
                <TableCell
                  sx={{ textAlign: "right", fontVariantNumeric: "tabular-nums", display: { xs: "none", sm: "table-cell" } }}
                >
                  {row.communities?.length ?? 0}
                </TableCell>
                <TableCell sx={{ textAlign: "right", width: 56 }}>
                  <Tooltip title="Ver comunidades">
                    <IconButton
                      aria-label={`Ver las comunidades de ${row.name}`}
                      onClick={() => verComunidades(row)}
                      sx={{ color: "primary.main" }}
                    >
                      <ChevronRightIcon />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  }

  return (
    <Container maxWidth={false} disableGutters>
      <PageHeader
        titulo={department ? `Municipios de ${department.name}` : "Municipios"}
        subtitulo="Elija un municipio para ver sus comunidades"
        icono={<FontAwesomeIcon icon={faMapLocationDot} />}
        acciones={
          <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={() => navigate(`${ruta}/locations`)}>
            Departamentos
          </Button>
        }
      />
      <Paper sx={{ p: { xs: 2, sm: 3 } }}>{contenido}</Paper>

      {editando ? (
        <DialogoAbreviatura
          municipio={editando}
          onCerrar={() => setEditando(null)}
          onGuardado={(nueva) => {
            aviso.exito(`Abreviatura de ${editando.name} guardada: ${nueva}`);
            setEditando(null);
            setRecargar((actual) => !actual);
          }}
        />
      ) : null}
    </Container>
  );
};

export default Municipalities;
